import Icon from '@/components/snippets/icon/icon'
import { ANCHOR_TAGS, AccountSectionWrapper } from './AccountSectionWrapper'
import { useState } from 'preact/hooks'
import { PageModal } from './AccountModal'
import { useToggle } from '../hooks/useToggle'
import { FormAddMeasurements } from './FormAddMeasurements'
import { Suspense } from './Suspense'
import { useAccountLayoutContext } from './AccountProfileContext'
import { useRefetch } from '../hooks/useRefetch'
import { accountTranslations } from '../helpers/translations'
import LoaderSpin from '@/components/snippets/loader-spin/loader-spin'

/**
 *  @typedef {import("../types/modal").ModalConfig} ModalConfig
 */

/**
 *
 * @param {{children: Readonly<React.ReactNode>}}  props
 */
const MeasurementListRowWrapper = ({ children }) => {
  return (
    <div className={'flex flex-row '}>
      <div
        className={'my-auto flex flex-shrink-0 flex-row gap-2 md:gap-4 rounded-lg border border-solid border-primary-blue p-2 md-max:text-xs'}
      >
        {children}
      </div>
      <div className={'my-auto w-full border border-solid border-primary-blue'}></div>
    </div>
  )
}

const MeasurementListRowShowMore = ({ props }) => {
  return (
    <MeasurementListRowWrapper>
      <div onClick={() => props.fetchMore(props.page)} className={'cursor-pointer text-primary-blue relative'}>
        <span className={props.isFetching ? 'invisible relative' : ''}>Show older entries... </span>{props.isFetching && <LoaderSpin />}
      </div>
    </MeasurementListRowWrapper>
  )
}

const MeasurementListRow = ({ row, handleDelete }) => {
  const deleteMeasurement = async () => {
    await handleDelete(row.id)
  }
  return (
    <MeasurementListRowWrapper>
      <div>{new Date(row.interacted_at).toDateString()}</div>
      <div>
        Size: {row?.meta?.measurement?.size_shoe?.unit} {row?.meta?.measurement?.size_shoe?.value}
      </div>
      <div>Width: {row?.meta?.measurement?.width_shoe?.value}</div>
      <div onClick={deleteMeasurement} className={'m-auto cursor-pointer'}>
        <Icon id="delete" className={'my-auto h-4'} />
      </div>
    </MeasurementListRowWrapper>
  )
}

const AccountMeasurementList = ({ handleDelete, sectionData, fetchMore, page, isFetching }) => {
  return (
    <>
      {sectionData.measurements.length ? (
        <div className={'relative flex flex-col gap-4 transition-all z-0'}>
          {sectionData.measurements.sort((a, b) => new Date(b.interacted_at) - new Date(a.interacted_at)).map((measurement) => (
            <MeasurementListRow row={measurement} handleDelete={handleDelete} />
          ))}
          {(sectionData.meta?.to || 0) < (sectionData.meta?.total || 0) && (
            <MeasurementListRowShowMore props={{ fetchMore, page, isFetching }} />
          )}
          <AccountMeasurementIcon />
        </div>
      ) : (
        <>No measurements have been recorded for this Child</>
      )}
    </>
  )
}

/**
 * Account Measurements Section
 * Displays the measurements recorded for the selected Child Profile.
 * Includes the Page modal in add a new measurement record.
 *
 * @param {*} param0
 * @returns
 */
export const AccountMeasurements = ({ props }) => {
  /** Fetch measurements */
  const { omneoClient } = useAccountLayoutContext()
  const [sectionData, setSectionData] = useState({
    measurements: [],
    meta: {}
  })
  const { refetch, handleRefetch } = useRefetch()
  const [isFetching, setIsFetching] = useState(false)
  const { isActive, handleToggle } = useToggle(false)
  const handleDelete = async (id) => {
    await omneoClient.profileClient.deleteChildInteractionById(props.connectionId, id)
    handleRefetch()
  }

  const [page, setPage] = useState(1)
  const pageSize = 9

  const fetchMeasurements = async (pageNumber) => {
    setIsFetching(true)
    const { data: getChildMeasurements, meta } = await omneoClient.profileClient.getChildMeasurements(props.connectionId, pageNumber, pageSize)
    setSectionData((data) => ({ measurements: pageNumber === 1 ? getChildMeasurements : [...data.measurements, ...getChildMeasurements], meta }))
    setPage(pageNumber + 1)
    setIsFetching(false)
  }

  return (
    <div id={ANCHOR_TAGS.MEASUREMENTS.tag} className={'h-full w-full bg-white'}>
      <Suspense
        refetch={refetch}
        props={{
          asyncCallback: () => fetchMeasurements(1)
        }}
      >
        <PageModal
          props={{
            isActive,
            handleToggle,
            modalTitle: accountTranslations.kids_profiles.measurements.cta,
            modal: <FormAddMeasurements handleToggle={handleToggle} handleUpdateView={handleRefetch} connectionId={props.connectionId} />
          }}
        >
          <AccountSectionWrapper
            anchorTag={ANCHOR_TAGS.MEASUREMENTS.tag}
            title={accountTranslations.kids_profiles.measurements.title}
            showDivider={true}
            cta={{
              hook: ANCHOR_TAGS.MEASUREMENTS.tag,
              title: accountTranslations.kids_profiles.measurements.cta,
              icon: 'plus',
              callback: handleToggle
            }}
          >
            <AccountMeasurementList
              sectionData={sectionData}
              handleDelete={handleDelete}
              fetchMore={fetchMeasurements}
              page={page}
              isFetching={isFetching}
            />
          </AccountSectionWrapper>
        </PageModal>
      </Suspense>
    </div>
  )
}

export const AccountMeasurementIcon = () => {
  return (
    <div className={'absolute top-0 bottom-0 right-0 w-auto -z-10 max-w-1/4 md:max-w-1/3 overflow-hidden'}>
    <svg xmlns="http://www.w3.org/2000/svg" width="210" height="478" viewBox="0 0 210 478" fill="none" className={'w-auto h-full'}>
      <path
        d="M210 209.123C210 119.498 165 -0.00196034 90 -0.00195706C15 -0.00195378 -1.60133e-05 111.656 -1.37117e-05 164.311C-1.17529e-05 209.123 15 235.264 30 261.404C45 287.545 60 313.686 60 358.498L180 358.498C180 358.498 210 268.593 210 209.123ZM120 477.998C153.094 477.998 180 451.204 180 418.248L180 388.373L60 388.373L60 418.248C60 451.204 86.9063 477.998 120 477.998Z"
        fill="#3398FF"
        fillOpacity="0.1"
      />
    </svg>
    </div>
  )
}
